import { deepCopy, isEqual } from '@lhb/func';
import { ControlType, OTHER_TYPE_ID } from '../../DynamicComponent/config';

const parse = (value: any): any => {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch (_) { return undefined; }
};
const object = (value: any) => value !== null && typeof value === 'object' && !Array.isArray(value);
export const hasFranchiseePrefillValue = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return !!value.trim();
  if (Array.isArray(value)) return value.length > 0 && value.some(hasFranchiseePrefillValue);
  if (object(value)) return Object.values(value).some(hasFranchiseePrefillValue);
  return typeof value !== 'number' || Number.isFinite(value);
};

/** 只遍历字段及分类树，不把子表单的字段提升为顶层同 code 候选。 */
const fieldsIn = (items: any[]): any[] => (Array.isArray(items) ? items : []).reduce((all, item) => {
  if (!item || typeof item !== 'object') return all;
  if (item.identification) all.push(item);
  all.push(...fieldsIn(item.propertyConfigVOList), ...fieldsIn(item.childList));
  return all;
}, []);

const restrictionOf = (field: any) => ({ ...(parse(field.restriction) || {}), ...(parse(field.templateRestriction) || {}) });
const derivedCodes = new Set(['ymNBJMLL', 'ymPXF', 'ymTJMDXZ', 'ymSQLX', 'customerQualityScore', 'estimatedDailyRevenue']);
// 这些控件由地址、预测或关联流程生成，不能沿用来源记录的旧结果。
const derivedTypes = new Set([
  ControlType.AREA.value, ControlType.SURROUND_SEARCH.value, ControlType.FOOTPRINT.value,
  ControlType.BUSINESS_PLANNING.value, ControlType.SALE_AMOUNT.value, ControlType.MATCH_BUSINESS_CIRCLE.value,
  ControlType.DAILY_FLOW_PREDICT.value, ControlType.REFERENCE_CONVERSION.value, ControlType.REFERENCE_RENT.value,
  ControlType.PROFIT_CAlCULATION_JNP.value, ControlType.ESTIMATE_SALES_YHT.value,
  ControlType.POPULATION_FLOW_YHT.value, ControlType.ESTIMATE_SALES_CONCLUSION_YHT.value,
  ControlType.ESTIMATE_SALES_TWO_YHT.value, ControlType.ESTIMATE_SALES_DELIVERY_YHT.value,
  ControlType.AI_PREDICT_ENTRY_RATE.value, ControlType.AREA_PROTECTION.value, ControlType.ASSOCIATED_RECORDS.value,
  ControlType.RECOMMEND_CHANCE_POINT.value,
]);

export const isFranchiseePrefillWritable = (field: any, supportDirectApproval = 2): boolean => {
  if (!field?.identification || derivedCodes.has(field.identification) || derivedTypes.has(field.controlType)) return false;
  const restriction = restrictionOf(field);
  if (Array.isArray(restriction.expr) && restriction.expr.length) return false;
  if (field.disabled || field.readOnly || field.readonly || restriction.readOnly || restriction.readonly) return false;
  // 审批表单参照 access；普通表单参照模板 restriction，与原控件禁用口径一致。
  return supportDirectApproval === 1 ? field.access === 2 : !restriction.disable;
};

/** 原始详情地址允许历史纯文本；JSON 的 null/空对象/坏结构不能充当有效地址。 */
export const getFranchiseeAddress = (detail: any): string => {
  const values = fieldsIn(detail?.propertyGroupVOList).filter(item => item.identification === 'basicAddress').map(item => {
    const raw = item.textValue;
    const value = parse(raw);
    if (object(value)) return [value.address, value.poiName].find(text => typeof text === 'string' && text.trim())?.trim() || '';
    if (typeof value === 'string') return value.trim();
    if (value !== undefined || typeof raw !== 'string') return '';
    const text = raw.trim();
    return /^[\[{\"]/.test(text) || /^(null|undefined)$/i.test(text) ? '' : text;
  }).filter(Boolean);
  return values.length && values.every(value => value === values[0]) ? values[0] : '';
};

const validId = (value: any) => (typeof value === 'string' && !!value.trim()) || (typeof value === 'number' && Number.isFinite(value));
const sameId = (left: any, right: any) => validId(left) && validId(right) && String(left) === String(right);

const selectedOption = (value: any, target: any): any => {
  if (!object(value) || !validId(value.selectedId)) return undefined;
  const restriction = restrictionOf(target);
  const withOther = restriction.withOther || (Array.isArray(parse(target.restriction)) && parse(target.restriction).includes('withOther'));
  if (sameId(value.selectedId, OTHER_TYPE_ID)) {
    return withOther ? { selectedId: OTHER_TYPE_ID, name: '其他', ...(typeof value.input === 'string' ? { input: value.input } : {}) } : undefined;
  }
  const options = (Array.isArray(target.propertyConfigOptionVOList) ? target.propertyConfigOptionVOList : []).filter(item => sameId(item.id, value.selectedId));
  return options.length === 1 ? { selectedId: options[0].id, name: options[0].name } : undefined;
};

const textTypes = new Set([
  ControlType.INPUT.value, ControlType.TEXT_AREA.value,
  ControlType.ESTIMATE_SALES_PERSONAL_YHT.value, ControlType.ESTIMATE_SALES_MT_MEAN_YHT.value,
  ControlType.ESTIMATE_SALES_MT_MAX_YHT.value,
]);
const knownTypes = new Set(Object.values(ControlType).map(item => item.value));

/** 返回目标 textValue；undefined 表示不覆盖目标默认值。 */
const convert = (source: any, target: any, supportDirectApproval: number): any => {
  if (source.controlType !== target.controlType || !isFranchiseePrefillWritable(target, supportDirectApproval)) return undefined;
  const raw = source.textValue;
  if (!hasFranchiseePrefillValue(raw)) return undefined;
  const type = target.controlType;
  if (textTypes.has(type)) return typeof raw === 'string' && raw.trim() ? raw : undefined;
  if (type === ControlType.TIME.value) {
    return typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw) && !Number.isNaN(Date.parse(raw)) ? raw : undefined;
  }
  if (type === ControlType.ADDRESS.value) {
    const address = getFranchiseeAddress({ propertyGroupVOList: [{ identification: 'basicAddress', textValue: raw }] });
    return address ? JSON.stringify({ address, poiName: address }) : undefined;
  }
  const value = parse(raw);
  if (!hasFranchiseePrefillValue(value)) return undefined;
  switch (type) {
    case ControlType.INPUT_NUMBER.value: {
      const numberValue = object(value) ? value.value : value;
      if (!['string', 'number'].includes(typeof numberValue) || !hasFranchiseePrefillValue(numberValue) || !Number.isFinite(Number(numberValue))) return undefined;
      return JSON.stringify({ value: numberValue, suffix: restrictionOf(target).suffixOptionList?.[0]?.name ?? null });
    }
    case ControlType.SINGLE_RADIO.value: {
      const option = selectedOption(value, target);
      return option ? JSON.stringify(option) : undefined;
    }
    case ControlType.CHECK_BOX.value: {
      if (!Array.isArray(value) || !value.length) return undefined;
      const options = value.map(item => selectedOption(item, target));
      return options.every(Boolean) ? JSON.stringify(options) : undefined;
    }
    case ControlType.UPLOAD.value:
      return Array.isArray(value) && value.length && value.every(item => object(item) && typeof item.name === 'string' && typeof item.url === 'string' && item.url.trim())
        ? JSON.stringify(value.map(item => ({ name: item.name, url: item.url }))) : undefined;
    case ControlType.TREE_SELECT.value: {
      const restriction = restrictionOf(target);
      if (!object(value) || !sameId(value.dataOrigin, restriction.dataOrigin) || !Array.isArray(value.optionList) || !value.optionList.length) return undefined;
      if (!restriction.multiple && value.optionList.length > 1) return undefined;
      return value.optionList.every(item => object(item) && validId(item.selectedId) && typeof item.name === 'string') ? JSON.stringify(value) : undefined;
    }
    case ControlType.SUB_FORM.value:
      return convertSubForm(value, source, target);
    case ControlType.COMPANY_MEMBER.value:
      return Array.isArray(value) && value.length && value.every(item => object(item) && validId(item.value) && typeof item.label === 'string') ? JSON.stringify(value) : undefined;
    case ControlType.CUSTOM_AREA.value:
      return object(value) && ['provinceId', 'cityId', 'districtId'].some(key => validId(value[key])) ? JSON.stringify(value) : undefined;
    case ControlType.LEASE_TERM.value:
      return object(value) && typeof value.startDate === 'string' && typeof value.endDate === 'string' && !Number.isNaN(Date.parse(value.startDate)) && !Number.isNaN(Date.parse(value.endDate)) ? JSON.stringify(value) : undefined;
    default:
      // 其他现有复合控件沿用它们的 JSON 结构；未知控件不能把损坏值送入回显。
      return knownTypes.has(type) && (object(value) || Array.isArray(value)) ? JSON.stringify(deepCopy(value)) : undefined;
  }
};

/** 详情子表单采用带 row 的序列化字段；输出保留目标字段 ID，供既有回显和提交转换使用。 */
const convertSubForm = (value: any, source: any, target: any): any => {
  if (!Array.isArray(value) || !value.length || !value.every(item => object(item) && Number.isInteger(item.row) && item.row >= 0 && item.identification)) return undefined;
  const targetChildren = Array.isArray(target.formConfigList) ? target.formConfigList : [];
  const rows = Array.from(new Set(value.map(item => item.row))).sort((a: number, b: number) => a - b);
  const restriction = restrictionOf(target);
  if (restriction.maxRows && rows.length > restriction.maxRows) return undefined;
  const result: any[] = [];
  let applied = false;
  rows.forEach((row, rowIndex) => {
    targetChildren.forEach(child => {
      const candidates = value.filter(item => item.row === row && item.identification === child.identification);
      const sourceConfig = (Array.isArray(source.formConfigList) ? source.formConfigList : []).filter(item => item.identification === child.identification);
      // 子表单 Item 继承父级 access；子项自身没有流程 access，沿用 disabled 配置。
      const converted = candidates.map(item => convert({ ...(sourceConfig.length === 1 ? sourceConfig[0] : {}), ...item }, child, 2));
      const valid = converted.length > 0 && converted.every(item => item !== undefined && isEqual(item, converted[0]));
      if (valid) applied = true;
      // 来源缺失或无效时保留目标子字段已初始化的默认值及只读结果。
      const textValue = valid ? converted[0] : child.textValue;
      if (!hasFranchiseePrefillValue(textValue)) return;
      result.push({ id: child.id, propertyId: child.propertyId, identification: child.identification, controlType: child.controlType, row: rowIndex, textValue: deepCopy(textValue) });
    });
  });
  return applied ? JSON.stringify(result) : undefined;
};

/** 目标先完成默认值初始化；这里只写 textValue，不复制源配置、字段 ID 或审批状态。 */
export const applyFranchiseePrefill = (sourceGroups: any[], targetFields: any[], supportDirectApproval = 2): { identifications: string[]; count: number } => {
  const sourceFields = fieldsIn(sourceGroups);
  const applied = new Set<string>();
  fieldsIn(targetFields).forEach(target => {
    const candidates = sourceFields.filter(source => source.identification === target.identification);
    const values = candidates.map(source => convert(source, target, supportDirectApproval));
    // 重复来源必须全部有值且转换结果一致，冲突时不随意采用第一条。
    if (!values.length || values.some(value => value === undefined) || !values.every(value => isEqual(value, values[0]))) return;
    target.textValue = deepCopy(values[0]);
    applied.add(target.identification);
    if (process.env.NODE_ENV === 'development') {
      console.log('[加盟商字段预填]', {
        identification: target.identification,
        controlType: target.controlType,
        sourceFields: deepCopy(candidates),
        targetField: deepCopy(target),
      });
    }
  });
  const identifications = Array.from(applied);
  return { identifications, count: identifications.length };
};


